import { useState, useEffect, useCallback, useRef } from 'react';
import { type User, type PhotoTagEntityType, isPlannerOrAbove } from '@peles/shared';
import {
  usePhoto,
  usePhotoDownloadUrl,
  useUpdatePhoto,
  useDeletePhoto,
  useAddPhotoTag,
  useRemovePhotoTag,
} from '../../hooks/usePhotos';
import { useEvents } from '../../hooks/useEvents';
import { useVenues } from '../../hooks/useVenues';
import { useUsers } from '../../hooks/useUsers';
import { useDialog } from '../../context/DialogContext';

interface PhotoDetailPanelProps {
  photoId: string;
  onClose: () => void;
  currentUser: User;
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  EVENT: 'Event',
  VENUE: 'Venue',
  VENDOR: 'Vendor',
  USER: 'User',
};

export function PhotoDetailPanel({ photoId, onClose, currentUser }: PhotoDetailPanelProps) {
  const { data: photo, isLoading } = usePhoto(photoId);
  const { data: urlData } = usePhotoDownloadUrl(photoId);
  const updatePhoto = useUpdatePhoto();
  const deletePhoto = useDeletePhoto();
  const addTag = useAddPhotoTag();
  const removeTag = useRemovePhotoTag();
  const { data: events = [] } = useEvents();
  const { data: venues = [] } = useVenues();
  const { data: users = [] } = useUsers();
  const { confirm } = useDialog();

  const [caption, setCaption] = useState('');
  const [originalCaption, setOriginalCaption] = useState('');
  const [saved, setSaved] = useState(false);
  const [tagEntityType, setTagEntityType] = useState<string>('');
  const [tagEntityId, setTagEntityId] = useState('');
  const [videoError, setVideoError] = useState(false);
  const prevPhotoId = useRef<string | null>(null);

  useEffect(() => {
    if (photo) {
      setCaption(photo.caption || '');
      setOriginalCaption(photo.caption || '');
      setSaved(false);
      if (prevPhotoId.current !== photo.id) {
        setVideoError(false);
        prevPhotoId.current = photo.id;
      }
    }
  }, [photo]);

  const dirty = caption !== originalCaption;

  const canEdit =
    photo &&
    (photo.uploadedByUserId === currentUser.id || isPlannerOrAbove(currentUser.systemRole));

  const handleSave = useCallback(() => {
    updatePhoto.mutate(
      { id: photoId, updates: { caption: caption || null } },
      {
        onSuccess: (updated) => {
          setCaption(updated.caption || '');
          setOriginalCaption(updated.caption || '');
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  }, [photoId, caption, updatePhoto]);

  const handleDelete = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Delete Photo',
      message: 'Delete this photo permanently? This cannot be undone.',
      confirmLabel: 'Delete',
    });
    if (confirmed) {
      deletePhoto.mutate(photoId, { onSuccess: onClose });
    }
  }, [photoId, deletePhoto, onClose, confirm]);

  const handleAddTag = useCallback(() => {
    if (!tagEntityType || !tagEntityId) return;
    addTag.mutate(
      {
        photoId,
        tag: {
          entityType: tagEntityType as PhotoTagEntityType,
          entityId: tagEntityId,
        },
      },
      {
        onSuccess: () => {
          setTagEntityType('');
          setTagEntityId('');
        },
      },
    );
  }, [photoId, tagEntityType, tagEntityId, addTag]);

  const entityOptions =
    tagEntityType === 'EVENT'
      ? events.map((e) => ({ id: e.id, label: e.name }))
      : tagEntityType === 'VENUE'
        ? venues.map((v) => ({ id: v.id, label: v.name }))
        : tagEntityType === 'USER'
          ? users.map((u) => ({ id: u.id, label: u.name }))
          : [];

  if (isLoading) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    );
  }

  if (!photo) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Photo not found</p>
      </div>
    );
  }

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold flex-1">Photo Details</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {saved && (
        <div className="mb-4 px-3 py-2 bg-green-50 text-green-700 text-sm rounded">
          Changes saved successfully
        </div>
      )}

      {/* Media preview */}
      <div className="mb-4 bg-gray-100 rounded-lg overflow-hidden">
        {urlData?.downloadUrl ? (
          photo.mimeType.startsWith('video/') ? (
            photo.transcodeStatus === 'PENDING' || photo.transcodeStatus === 'PROCESSING' ? (
              <div className="h-48 flex flex-col items-center justify-center gap-3 text-gray-500 text-sm px-4">
                <svg
                  className="animate-spin h-8 w-8 text-primary-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="text-center">Transcoding video for browser playback…</p>
              </div>
            ) : videoError ? (
              <div className="h-48 flex flex-col items-center justify-center gap-2 text-gray-500 text-sm px-4">
                <p className="text-center">This video format is not supported by your browser.</p>
                <a
                  href={urlData.downloadUrl}
                  download={photo.fileName}
                  className="text-primary-600 hover:underline text-xs"
                >
                  Download to play locally
                </a>
              </div>
            ) : (
              <video
                key={photoId}
                src={urlData.transcodedDownloadUrl || urlData.downloadUrl}
                controls
                className="w-full max-h-64"
                preload="metadata"
                onError={() => setVideoError(true)}
              />
            )
          ) : (
            <img
              src={urlData.downloadUrl}
              alt={photo.caption || photo.fileName}
              className="w-full max-h-64 object-contain"
            />
          )
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
            Loading media...
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* File info */}
        <div className="text-xs text-gray-500 space-y-1">
          <p>{photo.fileName}</p>
          <p>
            {(photo.fileSizeBytes / 1024).toFixed(0)} KB &middot; {photo.mimeType}
          </p>
          <p>Uploaded by {photo.uploadedByName || 'Unknown'}</p>
          <p>{new Date(photo.createdAt).toLocaleDateString()}</p>
          {photo.takenAt && <p>Taken on {new Date(photo.takenAt).toLocaleDateString()}</p>}
          {photo.latitude != null && photo.longitude != null && (
            <p>
              <a
                href={`https://www.google.com/maps?q=${photo.latitude},${photo.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline"
              >
                View location on map
              </a>
            </p>
          )}
        </div>

        {/* Caption */}
        <div>
          <label htmlFor="photo-caption" className="block text-sm font-medium text-gray-700 mb-1">
            Caption
          </label>
          <textarea
            id="photo-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Add a caption..."
            rows={2}
            disabled={!canEdit}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none disabled:bg-gray-50"
          />
        </div>

        {canEdit && (
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={!dirty || updatePhoto.isPending}
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updatePhoto.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}

        {/* Tags */}
        <div className="pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Tags ({photo.tags.length})</h3>
          {photo.tags.length === 0 ? (
            <p className="text-sm text-gray-500">No tags</p>
          ) : (
            <ul className="space-y-1 mb-3">
              {photo.tags.map((tag) => (
                <li
                  key={tag.id}
                  className="flex items-center justify-between px-3 py-1.5 bg-gray-50 rounded text-sm"
                >
                  <span>
                    <span className="text-xs font-medium text-primary-600 mr-1">
                      {ENTITY_TYPE_LABELS[tag.entityType] || tag.entityType}
                    </span>
                    <span className="text-gray-600">{tag.entityName || tag.entityId}</span>
                  </span>
                  <button
                    onClick={() => removeTag.mutate({ photoId, tagId: tag.id })}
                    className="text-gray-400 hover:text-red-500 text-xs"
                    aria-label="Remove tag"
                  >
                    &times;
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Add tag */}
          <div className="flex gap-2">
            <select
              value={tagEntityType}
              onChange={(e) => {
                setTagEntityType(e.target.value);
                setTagEntityId('');
              }}
              className="px-2 py-1.5 border border-gray-300 rounded text-sm flex-1"
            >
              <option value="">Type...</option>
              <option value="EVENT">Event</option>
              <option value="VENUE">Venue</option>
              <option value="USER">User</option>
            </select>
            <select
              value={tagEntityId}
              onChange={(e) => setTagEntityId(e.target.value)}
              disabled={!tagEntityType || entityOptions.length === 0}
              className="px-2 py-1.5 border border-gray-300 rounded text-sm flex-1 disabled:bg-gray-50"
            >
              <option value="">Select...</option>
              {entityOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddTag}
              disabled={!tagEntityType || !tagEntityId || addTag.isPending}
              className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Publish Controls */}
      {canEdit && photo && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Publish to Website</h3>
          <div className="space-y-3">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-gray-600">Show on public gallery</span>
              <input
                type="checkbox"
                checked={photo.isPublic}
                onChange={(e) =>
                  updatePhoto.mutate({
                    id: photoId,
                    updates: { isPublic: e.target.checked },
                  })
                }
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm text-gray-600">Feature on landing page</span>
              <input
                type="checkbox"
                checked={photo.showOnLanding}
                onChange={(e) =>
                  updatePhoto.mutate({
                    id: photoId,
                    updates: { showOnLanding: e.target.checked, isPublic: true },
                  })
                }
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
            </label>
          </div>
        </div>
      )}

      {/* Danger Zone */}
      {canEdit && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-red-600 mb-3">Danger Zone</h3>
          <button
            onClick={() => void handleDelete()}
            disabled={deletePhoto.isPending}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {deletePhoto.isPending ? 'Deleting...' : 'Delete Photo'}
          </button>
        </div>
      )}
    </div>
  );
}
